import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { sendRequest } from '../services/api';
// reCAPTCHA removed
import { trackEvent } from '../analytics/analytics';
import { sanitizePlainText, isValidEmail, isValidDutchPhone, isValidName, clamp } from '../utils/validation';

const DemoLessonSection = styled.section`
  padding: 100px 0;
  background: #fff;
  color: var(--primary-blue);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const ContactFormContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--primary-blue);
`;

const RequiredStar = styled.span`
  color: #ec5c2c;
  margin-left: 2px;
`;

const FormTitle = styled.h4`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-blue);
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: var(--primary-orange);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;


const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const WrapInput = styled.div`
  position: relative;
  flex: 1;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
  
  &.validate-input {
    position: relative;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 1rem;
  background: #f8f9fa;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ec5c2c;
    background: white;
    box-shadow: 0 0 0 3px rgba(236, 92, 44, 0.1);
  }
  
  &::placeholder {
    color: #6c757d;
  }
`;

const ShadowInput = styled.span`
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(45deg, #ec5c2c, #ff7043);
  border-radius: 1px;
  transform: scaleX(0);
  transition: transform 0.3s ease;
  
  ${FormInput}:focus + & {
    transform: scaleX(1);
  }
`;

const SelectContainer = styled.div`
  position: relative;
  margin-bottom: 2rem;
  /* Use FieldLabel component styling for labels; avoid overriding its color here */
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 1.2rem 1.5rem;
  border: 2px solid #e1e5e9;
  border-radius: 12px;
  font-size: 1rem;
  background: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #ec5c2c;
    background: white;
    box-shadow: 0 0 0 3px rgba(236, 92, 44, 0.1);
  }
`;

const FormButtonContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

const SubmitButton = styled.button<{ $loading?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem 3rem;
  background: linear-gradient(45deg, #ec5c2c, #ff7043);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  opacity: ${(p) => (p.$loading ? 0.85 : 1)};
  pointer-events: ${(p) => (p.$loading ? 'none' : 'auto')};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(236, 92, 44, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  i {
    font-size: 1.2rem;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const DemoLessonForm: React.FC = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: '',
    telephone: '',
    email: '',
    courseType: 'auto-manual'
  });
  
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    telephone: false,
    email: false,
    courseType: false
  });
  
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [hasSanitizeMismatch, setHasSanitizeMismatch] = useState(false);

  // no anti-bot delay

  // Use centralized, ReDoS-safe validators

  // Comprehensive form validation
  const validateForm = () => {
    const nameSan = sanitizePlainText(formData.name, 50);
    const phoneSan = sanitizePlainText(formData.telephone, 20);
    const emailSan = sanitizePlainText(formData.email, 254);
    const allowedCourse = ['auto-manual', 'auto-automatic'];

    // Mismatch policy: if sanitization changes the value, block
    const mismatch = (
      nameSan !== formData.name ||
      phoneSan !== formData.telephone ||
      emailSan !== formData.email
    );
    setHasSanitizeMismatch(mismatch);

    const errors = {
      name: mismatch || !isValidName(nameSan, 2, 50),
      telephone: mismatch || !isValidDutchPhone(phoneSan),
      email: mismatch || !isValidEmail(emailSan),
      courseType: !allowedCourse.includes(formData.courseType)
    };
    
    setValidationErrors(errors);
    const isValid = !Object.values(errors).some(error => error);
    setIsSubmitEnabled(isValid);
    return isValid;
  };

  // Handle input changes with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Do not sanitize or reformat on change; keep raw input for user to correct
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Real-time validation after updating state
    setTimeout(() => validateForm(), 0);
  };

  // Custom popup for success/error messages
  const showCustomPopup = (message: string, isSuccess: boolean) => {
    // Create custom popup instead of alert
    const popup = document.createElement('div');
    popup.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${isSuccess ? '#4CAF50' : '#f44336'};
      color: white;
      padding: 15px 20px;
      border-radius: 5px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-size: 14px;
      max-width: 300px;
    `;
    popup.textContent = message;
    document.body.appendChild(popup);
    
    setTimeout(() => {
      popup.remove();
    }, 4000);
  };

  // Basic submit lock to prevent double submission
  const [submitLocked, setSubmitLocked] = useState(false);
  const [started, setStarted] = useState(false);

  // Track view on mount
  useEffect(() => {
    trackEvent('form_demo_view', { path: typeof window !== 'undefined' ? window.location.pathname : undefined });
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    trackEvent('form_demo_submit_attempt', { courseType: formData.courseType });
    if (submitLocked) return;

    if (!validateForm()) {
      const errorMessage = hasSanitizeMismatch
        ? String(t('validation.invalid_input_generic'))
        : String(t('demo.error'));
      trackEvent('form_demo_validation_error', { courseType: formData.courseType });
      showCustomPopup(errorMessage, false);
      return;
    }

    // removed all anti-bot checks

    try {
      setSubmitLocked(true);
      // Sanitize and clamp before building payload
      const cleanName = clamp(sanitizePlainText(formData.name), 50);
      const cleanPhone = clamp(sanitizePlainText(formData.telephone), 20);
      const cleanEmail = clamp(sanitizePlainText(formData.email), 254);
      const subject = t('demo.email_subject', { defaultValue: `Demo lesson request - ${cleanName}` });
      const payload = {
        name: cleanName,
        email: cleanEmail,
        phone: cleanPhone,
        subject,
        message: `Course Type: ${formData.courseType}`,
        language: (typeof (t as any).language === 'string' ? (t as any).language : 'nl') || 'nl',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      };
      const resp = await sendRequest('sendEmail', payload, 'POST');
      const msg = (typeof resp === 'string' ? resp : (resp && (resp.message || resp.msg))) || String(t('demo.success'));
      showCustomPopup(msg, true);
      trackEvent('form_demo_submit_success', { courseType: formData.courseType });
      
      // Reset form
      setFormData({
        name: '',
        telephone: '',
        email: '',
        courseType: 'auto-manual'
      });
      setValidationErrors({
        name: false,
        telephone: false,
        email: false,
        courseType: false
      });
      setIsSubmitEnabled(false);
    } catch (error: any) {
      // Intentionally avoid console.error to keep browser console clean
      const status = error?.status;
      if (status === 429) {
        showCustomPopup('Je hebt de indieningslimiet bereikt. Neem contact met ons op via telefoon of WhatsApp.', false);
        trackEvent('form_demo_submit_error', { status: 429 });
      } else {
        showCustomPopup(t('demo.error_general'), false);
        trackEvent('form_demo_submit_error', { status: status || 0 });
      }
    } finally {
      setSubmitLocked(false);
    }
  };

  // Track first input start once
  const handleAnyInputStart = () => {
    if (!started) {
      setStarted(true);
      trackEvent('form_demo_start', { courseType: formData.courseType });
    }
  };

  return (
    <DemoLessonSection id="demo-lesson">
      <Container>
        <FormTitle>{t('demo.title')}</FormTitle>
        
        <ContactFormContainer className="contact-form-container">
          <form onSubmit={handleSubmit}>
            {/* Anti-bot fields removed */}
            <FormRow className="row">
              <WrapInput className={`wrap-input1 wrap-input-name validate-input col ${validationErrors.name ? 'is-invalid' : formData.name.length >= 2 ? 'is-valid' : ''}`}>
                <FieldLabel htmlFor="demo-lesson-form-name">{t('demo.name')}<RequiredStar>*</RequiredStar></FieldLabel>
                <FormInput 
                  id="demo-lesson-form-name" 
                  className="input1" 
                  type="text" 
                  required 
                  name="name"
                  value={formData.name}
                  onChange={(e) => { handleAnyInputStart(); handleInputChange(e); }}
                  onFocus={handleAnyInputStart}
                  placeholder={String(t('demo.name_placeholder', { defaultValue: 'e.g. John Smith' }))} 
                  minLength={2} 
                  maxLength={20}
                  style={{ borderColor: validationErrors.name ? '#f44336' : formData.name.length >= 2 ? '#4CAF50' : '#e1e5e9' }}
                />
                <ShadowInput className="shadow-input1" />
              </WrapInput>
              <WrapInput className={`wrap-input1 wrap-input-name validate-input col ${validationErrors.telephone ? 'is-invalid' : (!validationErrors.telephone && formData.telephone ? 'is-valid' : '')}`}>
                <input type="hidden" name="language" value="nl" />
                <FieldLabel htmlFor="demo-lesson-form-telephone">{t('demo.phone')}<RequiredStar>*</RequiredStar></FieldLabel>
                <FormInput 
                  id="demo-lesson-form-telephone" 
                  className="input1" 
                  type="text" 
                  name="telephone"
                  value={formData.telephone}
                  onChange={(e) => { handleAnyInputStart(); handleInputChange(e); }}
                  onFocus={handleAnyInputStart}
                  required
                  placeholder={String(t('demo.phone_placeholder', { defaultValue: 'e.g. +31 6 12345678' }))}
                  style={{ borderColor: validationErrors.telephone ? '#f44336' : (!validationErrors.telephone && formData.telephone ? '#4CAF50' : '#e1e5e9') }}
                />
                <ShadowInput className="shadow-input1" />
              </WrapInput>
            </FormRow>
            
            <WrapInput className={`wrap-input1 wrap-input-name validate-input ${validationErrors.email ? 'is-invalid' : (!validationErrors.email && formData.email ? 'is-valid' : '')}`}>
              <FieldLabel htmlFor="demo-lesson-form-email">{t('demo.email')}<RequiredStar>*</RequiredStar></FieldLabel>
              <FormInput 
                id="demo-lesson-form-email" 
                className="input1" 
                type="email" 
                required 
                name="email"
                value={formData.email}
                onChange={(e) => { handleAnyInputStart(); handleInputChange(e); }}
                onFocus={handleAnyInputStart}
                placeholder={String(t('demo.email_placeholder', { defaultValue: 'e.g. john@example.com' }))}
                style={{ borderColor: validationErrors.email ? '#f44336' : (!validationErrors.email && formData.email ? '#4CAF50' : '#e1e5e9') }}
              />
              <ShadowInput className="shadow-input1" />
            </WrapInput>
            
            <SelectContainer className={`select-course-type-container ${validationErrors.courseType ? 'is-invalid' : 'is-valid'}`}>
              <FieldLabel htmlFor="demo-lesson-form-course-type">{t('demo.service')}<RequiredStar>*</RequiredStar></FieldLabel>
              <FormSelect 
                id="demo-lesson-form-course-type" 
                className="round"
                name="courseType"
                value={formData.courseType}
                onChange={(e) => { handleAnyInputStart(); handleInputChange(e); }}
                onFocus={handleAnyInputStart}
                style={{ borderColor: validationErrors.courseType ? '#f44336' : '#4CAF50' }}
              >
                <option value="auto-manual">{t('demo.service_auto_manual')}</option>
                <option value="auto-automatic">{t('demo.service_auto_automatic')}</option>
              </FormSelect>
              <ShadowInput className="shadow-input1" />
            </SelectContainer>

            <FormButtonContainer className="container-contact1-form-btn">
              <SubmitButton 
                type="submit"
                className="contact1-form-btn"
                disabled={!isSubmitEnabled || submitLocked}
                aria-busy={submitLocked}
                aria-live="polite"
                $loading={submitLocked}
                style={{ 
                  opacity: (!isSubmitEnabled || submitLocked) ? 0.6 : 1,
                  cursor: (!isSubmitEnabled || submitLocked) ? 'not-allowed' : 'pointer'
                }}
              >
                {submitLocked ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8rem' }}>
                    <Spinner aria-hidden="true" />
                    <span>{t('modal.loading', { defaultValue: 'Please wait...' })}</span>
                  </span>
                ) : (
                  <span>
                    {t('demo.submit')}
                    <i className="fa fa-long-arrow-right" aria-hidden="true"></i>
                  </span>
                )}
              </SubmitButton>
            </FormButtonContainer>
          </form>
        </ContactFormContainer>
      </Container>
    </DemoLessonSection>
  );
};

export default DemoLessonForm;
